import api from "../lib/axios";

/**
 * Fetch exhibitor categories for a specific event and user.
 * @param {string|number} eventId
 * @param {string|number} userId
 * @param {string} token
 */
export const getExhibitorCategories = async (eventId, token) => {
  return api.get(`/exhibitor/category/list`, {
    params: {
      event_id: eventId,
      features: "true",
    },
    headers: {
      "ngrok-skip-browser-warning": "true",
    },
  });
};

/**
 * Fetch public exhibitor categories for a specific event (no auth required).
 * @param {string|number} eventId
 */
export const getPublicExhibitorCategories = async (eventId) => {
  return api.get(`/exhibitor/public/category/list`, {
    params: {
      event_id: eventId,
      features: "true",
    },
    headers: {
      "ngrok-skip-browser-warning": "true",
    },
  });
};

/**
 * Create a new exhibitor category.
 * @param {object} data
 * @param {string} token
 */
export const createExhibitorCategory = async (data, token) => {
  return api.post(`/exhibitor/category/create`, data, {
    headers: {
      "ngrok-skip-browser-warning": "true",
    },
  });
};

/**
 * Update an existing exhibitor category.
 * @param {object} data
 * @param {string} token
 */
export const updateExhibitorCategory = async (data, token) => {
  return api.put(`/exhibitor/category/update`, data, {
    headers: {
      "ngrok-skip-browser-warning": "true",
    },
  });
};

/**
 * Delete an exhibitor category.
 * @param {string|number} category_id
 * @param {string|number} eventId
 * @param {string|number} userId
 * @param {string} token
 */
export const deleteExhibitorCategory = async (
  category_id,
  eventId,
  token,
) => {
  return api.delete(`/exhibitor/category/delete`, {
    data: {
      category_id,
      event_id: eventId,
    },
    headers: {
      "ngrok-skip-browser-warning": "true",
    },
  });
};

/**
 * Delete multiple exhibitor categories.
 * @param {Array} category_ids
 * @param {string|number} eventId
 * @param {string} token
 */
export const deleteExhibitorCategories = async (
  category_ids,
  eventId,
  token,
) => {
  return api.delete(`/exhibitor/category/delete`, {
    data: {
      category_ids,
      event_id: eventId,
    },
    headers: {
      "ngrok-skip-browser-warning": "true",
    },
  });
};

/**
 * Fetch exhibitor event settings (unit, currency, etc.).
 * @param {string|number} eventId
 * @param {string} token
 */
export const getExhibitorEventSettings = async (eventId, token) => {
  return api.get(`/exhibitor/event-settings/${eventId}`, {
    headers: {
      "ngrok-skip-browser-warning": "true",
    },
  });
};

/**
 * Update exhibitor event settings (currency, unit, etc.).
 * @param {object} data
 * @param {string} token
 */
export const updateExhibitorEventSettings = async (data, token) => {
  return api.post(`/exhibitor/event-settings/update`, data, {
    headers: {
      "ngrok-skip-browser-warning": "true",
    },
  });
};
/**
 * Fetch the count of exhibitor categories for a specific event.
 * @param {string|number} eventId
 * @param {string} token
 */
const countPromises = new Map();

export const getExhibitorCategoryCount = async (eventId, token) => {
  const key = `${eventId}`;
  if (countPromises.has(key)) {
    return countPromises.get(key);
  }
  const promise = (async () => {
    try {
      return await api.get(`/exhibitor/category/count`, {
        params: {
          event_id: eventId,
        },
        headers: {
          "ngrok-skip-browser-warning": "true",
        },
      });
    } finally {
      countPromises.delete(key);
    }
  })();
  countPromises.set(key, promise);
  return promise;
};
/**
 * Fetch public exhibitor list for a specific event with search.
 * @param {string|number} eventId
 * @param {string} search
 */
export const getPublicExhibitors = async (eventId, search = "") => {
  return api.get(`/allexhibitor/site/list/${eventId}`, {
    params: {
      search: search,
    },
    headers: {
      "ngrok-skip-browser-warning": "true",
    },
  });
};

/**
 * Fetch public exhibitor details by ID.
 * @param {string|number} exhibitorId
 */
export const getPublicExhibitorDetail = async (exhibitorId) => {
  return api.get(`/allexhibitor/site/detail/${exhibitorId}`, {
    params: {
      promoBanner: "true",
      media: "true",
    },
    headers: {
      "ngrok-skip-browser-warning": "true",
    },
  });
};

export const getBoothMembers = async (eventId, exhibitorId, limit = 100) => {
  const res = await api.get(`/exhibitor/booth-member/list`, {
    params: { event_id: eventId, exhibitor_id: exhibitorId, limit },
    headers: {
      "Content-Type": "application/json",
      "ngrok-skip-browser-warning": "true",
    },
  });
  return res;
};

export const importExhibitors = async ({ file, eventId, reverse, skipExisting, importType }) => {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("event_id", eventId);
  formData.append("reverse", reverse);
  formData.append("skipExisting", skipExisting);
  formData.append("import_type", importType);
  const endpoint =
    importType === "booth_members" || importType === "booth_member"
      ? "/exhibitor/booth-member/import"
      : "/exhibitor/import";
  const res = await api.post(endpoint, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
      "ngrok-skip-browser-warning": "true",
    },
  });
  return res;
};

export const getAllExhibitors = async (eventId) => {
  const res = await api.get(`/allexhibitor/list?event_id=${eventId}&limit=1000&cat_details=true`, {
    headers: { "ngrok-skip-browser-warning": "true" },
  });
  return res;
};

export const getExhibitorTags = async (eventId) => {
  const res = await api.get(`/allexhibitor/tags`, {
    params: { event_id: eventId },
    headers: { "Content-Type": "application/json", "ngrok-skip-browser-warning": "true" },
  });
  return res;
};

export const createExhibitorTag = async (tagData, token) => {
  const res = await api.post(`/allexhibitor/tags`, tagData, {
    headers: { "Content-Type": "application/json", "ngrok-skip-browser-warning": "true" },
  });
  return res;
};

export const createExhibitor = async (formData, token) => {
  const res = await api.post(`/allexhibitor/create`, formData, {
    headers: { "ngrok-skip-browser-warning": "true" },
    transformRequest: [(d) => d],
  });
  return res;
};

export const updateExhibitor = async (exhibitorId, formData, token) => {
  const res = await api.put(`/allexhibitor/update/${exhibitorId}`, formData, {
    headers: { "ngrok-skip-browser-warning": "true" },
    transformRequest: [(d) => d],
  });
  return res;
};

export const deleteExhibitor = async (exhibitorId, eventId, token) => {
  const res = await api.delete(`/allexhibitor/delete/${exhibitorId}`, {
    data: { event_id: eventId },
    headers: { "Content-Type": "application/json", "ngrok-skip-browser-warning": "true" },
  });
  return res;
};

export const getAllExhibitors2 = async (queryString) => {
  const res = await api.get(`/allexhibitor/list?${queryString}`, {
    headers: {
      "ngrok-skip-browser-warning": "true",
    },
  });
  return res;
};

export const getExhibitorCategoriesWithBooths = async (eventId, token) => {
  return api.get(`/exhibitor/category/list`, {
    params: {
      event_id: eventId,
      booth: "true",
    },
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
};

export const uploadExhibitorFile = async (formData, token) => {
  const res = await api.post("/exhibitor/upload/file", formData, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
    transformRequest: [(d) => d],
  });
  return res;
};

export const saveExhibitorFormFields = async (payload, token) => {
  const res = await api.post("/exhibitor/form/fields/save", payload, {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      "ngrok-skip-browser-warning": "true",
    },
  });
  return res;
};

export const getExhibitorSettings = async (eventId) => {
  const res = await api.get("/exhibitor/settings/get", { params: { event_id: eventId }, withCredentials: true, headers: { "ngrok-skip-browser-warning": "true" } });
  return res;
};

export const updateExhibitorSettings = async (payload) => {
  const res = await api.put("/exhibitor/settings/update", payload, { withCredentials: true, headers: { "ngrok-skip-browser-warning": "true" } });
  return res;
};

export const getExhibitorSummary = async (eventId) => {
  const res = await api.get(`/exhibitor/summary`, {
    params: {
      event_id: eventId,
    },
    headers: {
      "Content-Type": "application/json",
      "ngrok-skip-browser-warning": "true",
    },
    withCredentials: true,
  });
  return res;
};
